/**
 * /principles — list + inline composer.
 *
 * Each row: principle key, statement, applies-to tags (HITL/HOTL/HOOL/agent_only
 * or department keys), version. Founder/admin sees a "+ Add principle"
 * composer at the bottom of the list. Drag-drop reorder is intentionally
 * out-of-scope here — Principles aren't ordered by canonical priority
 * (each has its own scope key), so re-ordering would imply a meaning the
 * upstream doesn't model.
 */
import type { Principle, PrincipleProposal } from '@chaos/types/constitution';
import { principleProposalSchema } from '@chaos/types/constitution';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Compass, Loader2, Plus, Tag, X } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { useToaster } from '../../../components/ui/toast';
import { constitutionApi, constitutionKeys } from '../../../lib/api/constitution';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

const AUTONOMY_TAGS = ['HITL', 'HOTL', 'HOOL', 'agent_only'] as const;

function PrinciplesRoute() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canEdit = role === 'founder' || role === 'admin';
  const [composing, setComposing] = React.useState(false);

  const principlesQ = useQuery({
    queryKey: constitutionKeys.principles('general'),
    queryFn: () => constitutionApi.listPrinciples('general'),
    staleTime: 30_000,
  });
  const principles: Principle[] = principlesQ.data?.principles ?? [];

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <Compass className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">Principles</h1>
          <p className="text-[12px] text-muted-foreground">
            Per-scope guidance every agent applies. Inform autonomy decisions.
          </p>
        </div>
        {canEdit ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setComposing((c) => !c)}
            className="ml-auto h-7"
          >
            {composing ? (
              <>
                <X className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Cancel
              </>
            ) : (
              <>
                <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Propose principle
              </>
            )}
          </Button>
        ) : null}
      </header>

      {composing && canEdit ? <PrincipleComposer onDone={() => setComposing(false)} /> : null}

      <div className="rounded border border-border bg-background">
        <ul className="divide-y divide-border">
          {principlesQ.isLoading ? (
            <li className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              Loading principles…
            </li>
          ) : null}
          {!principlesQ.isLoading && principles.length === 0 ? (
            <li className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              No principles yet.
            </li>
          ) : null}
          {principles.map((p) => (
            <PrincipleRow key={p.id} principle={p} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function PrincipleRow({ principle }: { principle: Principle }) {
  return (
    <li className="flex flex-col gap-1.5 px-3 py-3">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] text-muted-foreground">
          {principle.principleKey}
        </span>
        <span className="rounded border border-border bg-muted/30 px-1.5 py-0.5 font-mono text-[9px] tabular-nums text-muted-foreground">
          v{principle.version}
        </span>
        <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground/70">
          {new Date(principle.createdAt).toISOString().slice(0, 10)}
        </span>
      </div>
      <p className="text-[13px] text-foreground">{principle.statement}</p>
      {principle.appliesTo.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
          <Tag className="h-3 w-3" aria-hidden />
          {principle.appliesTo.map((scope) => (
            <span
              key={scope}
              className={cn(
                'rounded border border-border bg-muted/30 px-1.5 py-0.5 uppercase tracking-wider',
                AUTONOMY_TAGS.includes(scope as (typeof AUTONOMY_TAGS)[number])
                  ? 'text-foreground'
                  : 'text-muted-foreground',
              )}
            >
              {scope}
            </span>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function PrincipleComposer({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToaster();
  const [appliesInput, setAppliesInput] = React.useState('');
  const form = useForm<PrincipleProposal>({
    resolver: zodResolver(principleProposalSchema),
    defaultValues: {
      principleKey: '',
      statement: '',
      appliesTo: [],
      scope: 'general',
    },
  });
  const watchAppliesTo = form.watch('appliesTo') ?? [];

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (watchAppliesTo.includes(trimmed)) return;
    form.setValue('appliesTo', [...watchAppliesTo, trimmed], { shouldDirty: true });
  };
  const removeTag = (tag: string) => {
    form.setValue(
      'appliesTo',
      watchAppliesTo.filter((t) => t !== tag),
      { shouldDirty: true },
    );
  };

  const submit = useMutation({
    mutationFn: (input: PrincipleProposal) => constitutionApi.proposePrinciple(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: constitutionKeys.all });
      toast({ title: 'Principle proposal submitted' });
      onDone();
    },
    onError: (err: Error) => {
      toast({ title: 'Submit failed', description: err.message, variant: 'destructive' });
    },
  });
  const onSubmit = form.handleSubmit((values) => {
    submit.mutate(values);
  });

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-3 rounded border border-border bg-muted/20 p-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1 sm:col-span-1">
          <label htmlFor="principle-key" className="text-[12px] font-medium text-foreground">
            Principle key
          </label>
          <Input
            id="principle-key"
            placeholder="no_pii_in_logs"
            {...form.register('principleKey')}
            disabled={submit.isPending}
          />
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <label htmlFor="principle-statement" className="text-[12px] font-medium text-foreground">
            Statement
          </label>
          <Textarea
            id="principle-statement"
            rows={2}
            placeholder="Agents must never write raw PII into logs."
            {...form.register('statement')}
            disabled={submit.isPending}
          />
        </div>
      </div>

      <fieldset className="flex flex-col gap-1.5 border-0 p-0">
        <legend className="px-0 text-[12px] font-medium text-foreground">Applies to</legend>
        <div className="flex flex-wrap items-center gap-1">
          {AUTONOMY_TAGS.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => (watchAppliesTo.includes(tag) ? removeTag(tag) : addTag(tag))}
              className={cn(
                'rounded border px-2 py-0.5 text-[10px] uppercase tracking-wider transition-colors',
                watchAppliesTo.includes(tag)
                  ? 'border-foreground/40 bg-foreground/5 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
              )}
            >
              {tag}
            </button>
          ))}
          {watchAppliesTo
            .filter((t) => !AUTONOMY_TAGS.includes(t as (typeof AUTONOMY_TAGS)[number]))
            .map((custom) => (
              <span
                key={custom}
                className="inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-foreground"
              >
                {custom}
                <button
                  type="button"
                  onClick={() => removeTag(custom)}
                  aria-label={`Remove tag ${custom}`}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </span>
            ))}
          <div className="ml-1 inline-flex items-center gap-1">
            <Input
              value={appliesInput}
              onChange={(e) => setAppliesInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag(appliesInput);
                  setAppliesInput('');
                }
              }}
              placeholder="add tag…"
              className="h-7 w-32 text-[11px]"
            />
          </div>
        </div>
      </fieldset>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              Submitting…
            </>
          ) : (
            'Submit proposal'
          )}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} disabled={submit.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export const Route = createFileRoute('/_authenticated/principles/')({
  component: PrinciplesRoute,
});
